import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page } from "../_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ScrollText, LogIn, UserPlus, UserMinus, Edit, Trash2 } from "lucide-react";

const ICONS = {
  login: LogIn,
  signup: UserPlus,
  user_update: Edit,
  user_delete: UserMinus,
  doc_delete: Trash2,
};

const COLORS = {
  login: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  signup: "text-primary bg-primary/10 border-primary/20",
  user_update: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  user_delete: "text-red-400 bg-red-500/10 border-red-500/20",
  doc_delete: "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

export default function AdminLogs() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const load = () => {
    const params = { skip, limit };
    if (filter !== "all") params.action = filter;
    api.get("/admin/logs", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [filter, skip]); // eslint-disable-line

  return (
    <Page
      title="Logs de atividade"
      subtitle="Auditoria completa de eventos críticos do sistema."
      action={
        <Select value={filter} onValueChange={(v) => { setFilter(v); setSkip(0); }}>
          <SelectTrigger data-testid="logs-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os eventos</SelectItem>
            <SelectItem value="login">Logins</SelectItem>
            <SelectItem value="signup">Cadastros</SelectItem>
            <SelectItem value="user_update">Atualizações</SelectItem>
            <SelectItem value="user_delete">Exclusões usuário</SelectItem>
            <SelectItem value="doc_delete">Exclusões dados</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      {!data ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : data.items.length === 0 ? (
        <div className="p-12 rounded-xl border border-dashed border-border bg-card/40 text-center text-sm text-muted-foreground">
          <ScrollText className="w-8 h-8 mx-auto mb-3 opacity-50" />
          Nenhum log de atividade ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((log) => {
            const Icon = ICONS[log.action] || ScrollText;
            const color = COLORS[log.action] || "text-muted-foreground bg-secondary border-border";
            return (
              <div key={log.id} data-testid={`log-${log.id}`} className="p-4 rounded-xl border border-border bg-card flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-secondary border border-border">{log.action}</span>
                    <span className="text-sm">{log.email || log.user_id || "sistema"}</span>
                  </div>
                  {log.target && <div className="text-xs text-muted-foreground mt-1 font-mono">target: {log.target}</div>}
                  {log.meta && Object.keys(log.meta).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{JSON.stringify(log.meta)}</div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono shrink-0">
                  {new Date(log.created_at).toLocaleString("pt-BR")}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && data.total > limit && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Anterior</Button>
          <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + limit, data.total)} de {data.total}</span>
          <Button variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Próximo</Button>
        </div>
      )}
    </Page>
  );
}
